import * as React from 'react';
import isEqual from 'react-fast-compare';
import deepmerge from 'deepmerge';
import { FormikProvider } from './connect';
import warning from 'tiny-warning';
import {
  FormikActions,
  FormikConfig,
  FormikErrors,
  FormikState,
  FormikTouched,
  FormikValues,
  FormikProps,
} from './types';
import {
  isEmptyChildren,
  isFunction,
  isNaN,
  isPromise,
  isString,
  isInputEvent,
  setIn,
  setNestedObjectValues,
  getActiveElement,
  getIn,
  makeCancelable,
} from './utils';
import { hasBasename } from 'history/PathUtils';
import { has } from 'lodash';

export class Formik<Values = FormikValues> extends React.Component<
  FormikConfig<Values>,
  FormikState<Values>
  > {
  public static defaultProps = {
    validateOnChange: true,
    validateOnBlur: true,
    isInitialValid: false,
    enableReinitialize: false,
  };

  public initialValues: Values;
  public didMount: boolean;
  public hcCache: {
    [key: string]: (e: unknown | React.ChangeEvent<any>) => void;
  } = {};
  public hbCache: {
    [key: string]: (e: any) => void;
  } = {};
  public fields: {
    [field: string]: React.Component<any>;
  };
  public validator: any;
  public validatorSoft: any;
  public validatorWarning: any;
  
  constructor(props: FormikConfig<Values>) {
    super(props);
    this.state = {
      values: props.initialValues || ({} as any),
      errors: {},
      errorsSoft: {},
      warnings: {},
      touched: {},
      isSubmitting: false,
      isLastSubmitInvalid: false,
      isValidating: false,
      submitCount: 0,
      status: props.initialStatus,
    };
    this.didMount = false;
    this.fields = {};
    this.initialValues = props.initialValues || ({} as any);
    warning(
      !(props.component && props.render),
      'You should not use <Formik component> and <Formik render> in the same <Formik> component; <Formik render> will be ignored',
    );

    warning(
      !(props.component && props.children && !isEmptyChildren(props.children)),
      'You should not use <Formik component> and <Formik children> in the same <Formik> component; <Formik children> will be ignored',
    );

    warning(
      !(props.render && props.children && !isEmptyChildren(props.children)),
      'You should not use <Formik render> and <Formik children> in the same <Formik> component; <Formik children> will be ignored',
    );
  }

  public registerField = (name: string, Comp: React.Component<any>) => {
    this.fields[name] = Comp;
  }

  public unregisterField = (name: string) => {
    delete this.fields[name];
  }

  public componentDidMount() {
    this.didMount = true;
  }

  public componentWillUnmount() {
    // This allows us to prevent setting state on an
    // unmounted component. This can occur if Formik is in a modal, and submission
    // toggles show/hide, and validation of a blur field takes longer than validation
    // before a submit.
    // @see https://github.com/jaredpalmer/formik/issues/597
    // @see https://reactjs.org/blog/2015/12/16/ismounted-antipattern.html
    this.didMount = false;

    // Cancel validation on unmount.
    if (this.validator) {
      this.validator();
    }

    if (this.validatorSoft) {
      this.validatorSoft();
    }

    if (this.validatorWarning) {
      this.validatorWarning();
    }
  }

  public componentDidUpdate(prevProps: Readonly<FormikConfig<Values>>) {
    // If the initialValues change, reset the form
    if (
      this.props.enableReinitialize &&
      !isEqual(prevProps.initialValues, this.props.initialValues)
    ) {
      this.initialValues = this.props.initialValues;
      // @todo refactor to use getDerivedStateFromProps?
      this.resetForm(this.props.initialValues);
    }
  }

  public setErrors = (errors: FormikErrors<Values>) => {
    this.setState({ errors });
  }

  public setErrorsSoft = (errorsSoft?: FormikErrors<Values>) => {
    this.setState({ errorsSoft });
  }

  public setWarnings = (warnings?: FormikErrors<Values>) => {
    this.setState({ warnings });
  }
  public setTouched = (touched: FormikTouched<Values>) => {
    this.setState({ touched }, () => {
      if (this.props.validateOnBlur) {
        this.runValidations(this.state.values);
        this.runValidationsSoft(this.state.values);
        this.runValidationsWarning(this.state.values);
      }
    });
  }

  public setValues = (values: FormikState<Values>['values']) => {
    this.setState({ values }, () => {
      if (this.props.validateOnChange) {
        this.runValidations(values);
        this.runValidationsSoft(values);
        this.runValidationsWarning(values);
      }
    });
  }

  public setStatus = (status?: any) => {
    this.setState({ status });
  }

  public setError = (error: any) => {
    if (process.env.NODE_ENV !== 'production') {
      console.warn(
        `Warning: Formik\'s setError(error) is deprecated and may be removed in future releases. Please use Formik\'s setStatus(status) instead. It works identically. For more info see https://github.com/jaredpalmer/formik#setstatus-status-any--void`,
      );
    }
    this.setState({ error });
  }

  public setSubmitting = (isSubmitting: boolean) => {
    if (this.didMount) {
      this.setState({ isSubmitting, isLastSubmitInvalid: false });
    }
  }

  /**
   * Run field level validation
   */
  public validateField = (field: string): Promise<Object> => {
    this.setState({ isValidating: true });
    return this.runSingleFieldLevelValidation(
      field,
      getIn(this.state.values, field),
    ).then(error => {
      if (this.didMount) {
        this.setState({
          errors: setIn(this.state.errors, field, error),
          isValidating: false,
        });
      }
      return error;
    });
  }


  public validateFieldSoft = (field: string): Promise<Object> => {
    this.setState({ isValidating: true });
    return this.runSingleFieldLevelValidationSoft(
      field,
      getIn(this.state.values, field),
    ).then(error => {
      if (this.didMount) {
        this.setState({
          errorsSoft: setIn(this.state.errorsSoft, field, error),
          isValidating: false,
        });
      }
      return error;
    });
  }

  public validateFieldWarning = (field: string): Promise<Object> => {
    this.setState({ isValidating: true });
    return this.runSingleFieldLevelValidationWarning(
      field,
      getIn(this.state.values, field),
    ).then(warning => {
      if (this.didMount) {
        this.setState({
          warnings: setIn(this.state.warnings, field, warning),
          isValidating: false,
        });
      }
      return warning;
    });
  }

  public runSingleFieldLevelValidation = (
    field: string,
    value: void | string,
  ): Promise<string> => {
    return new Promise(resolve =>
      resolve(this.fields[field].props.validate(value)),
    ).then(x => x, e => e);
  }

  public runSingleFieldLevelValidationSoft = (
    field: string,
    value: void | string,
  ): Promise<string> => {
    return new Promise(resolve =>
      resolve(this.fields[field].props.validateSoft(value)),
    ).then(x => x, e => e);
  }

  public runSingleFieldLevelValidationWarning = (
    field: string,
    value: void | string,
  ): Promise<string> => {
    return new Promise(resolve =>
      resolve(this.fields[field].props.validateWarning(value)),
    ).then(x => x, e => e);
  }

  public runFieldLevelValidations(
    values: FormikValues,
  ): Promise<FormikErrors<Values>> {
    const fieldKeysWithValidation: string[] = Object.keys(this.fields).filter(
      f =>
        this.fields &&
        this.fields[f] &&
        this.fields[f].props.validate &&
        isFunction(this.fields[f].props.validate),
    );

    // Construct an array with all of the field validation functions
    const fieldValidations: Promise<string>[] =
      fieldKeysWithValidation.length > 0
        ? fieldKeysWithValidation.map(f =>
          this.runSingleFieldLevelValidation(f, getIn(values, f)),
        )
        : [Promise.resolve('DO_NOT_DELETE_YOU_WILL_BE_FIRED')]; // use special case ;)

    return Promise.all(fieldValidations).then((fieldErrorsList: string[]) =>
      fieldErrorsList.reduce(
        (prev, curr, index) => {
          if (curr === 'DO_NOT_DELETE_YOU_WILL_BE_FIRED') {
            return prev;
          }
          if (!!curr) {
            prev = setIn(prev, fieldKeysWithValidation[index], curr);
          }
          return prev;
        },
        {} as FormikErrors<Values>,
      ),
    );
  }

  public runFieldLevelValidationsSoft(
    values: FormikValues,
  ): Promise<FormikErrors<Values>> {
    const fieldKeysWithValidation: string[] = Object.keys(this.fields).filter(
      f =>
        this.fields &&
        this.fields[f] &&
        this.fields[f].props.validateSoft &&
        isFunction(this.fields[f].props.validateSoft),
    );

    // Construct an array with all of the field validation functions
    const fieldValidations: Promise<string>[] =
      fieldKeysWithValidation.length > 0
        ? fieldKeysWithValidation.map(f =>
          this.runSingleFieldLevelValidationSoft(f, getIn(values, f)),
        )
        : [Promise.resolve('DO_NOT_DELETE_YOU_WILL_BE_FIRED')]; // use special case ;)

    return Promise.all(fieldValidations).then((fieldErrorsList: string[]) =>
      fieldErrorsList.reduce(
        (prev, curr, index) => {
          if (curr === 'DO_NOT_DELETE_YOU_WILL_BE_FIRED') {
            return prev;
          }
          if (!!curr) {
            prev = setIn(prev, fieldKeysWithValidation[index], curr);
          }
          return prev;
        },
        {} as FormikErrors<Values>,
      ),
    );
  }

  public runFieldLevelValidationsWarning(
    values: FormikValues,
  ): Promise<FormikErrors<Values>> {
    const fieldKeysWithValidation: string[] = Object.keys(this.fields).filter(
      f =>
        this.fields &&
        this.fields[f] &&
        this.fields[f].props.validateWarning &&
        isFunction(this.fields[f].props.validateWarning),
    );

    // Construct an array with all of the field validation functions
    const fieldValidations: Promise<string>[] =
      fieldKeysWithValidation.length > 0
        ? fieldKeysWithValidation.map(f =>
          this.runSingleFieldLevelValidationWarning(f, getIn(values, f)),
        )
        : [Promise.resolve('DO_NOT_DELETE_YOU_WILL_BE_FIRED')]; // use special case ;)

    return Promise.all(fieldValidations).then((fieldErrorsList: string[]) =>
      fieldErrorsList.reduce(
        (prev, curr, index) => {
          if (curr === 'DO_NOT_DELETE_YOU_WILL_BE_FIRED') {
            return prev;
          }
          if (!!curr) {
            prev = setIn(prev, fieldKeysWithValidation[index], curr);
          }
          return prev;
        },
        {} as FormikErrors<Values>,
      ),
    );
  }

  public runValidateHandler(values: FormikValues): Promise<FormikErrors<Values>> {
    return new Promise(resolve => {
      const maybePromisedErrors = (this.props.validate as any)(values);
      if (maybePromisedErrors === undefined) {
        resolve({});
      } else if (isPromise(maybePromisedErrors)) {
        (maybePromisedErrors as Promise<any>).then(
          () => {
            resolve({});
          },
          errors => {
            resolve(errors);
          },
        );
      } else {
        resolve(maybePromisedErrors);
      }
    });
  }

  public runValidateHandlerSoft(values: FormikValues): Promise<FormikErrors<Values>> {
    return new Promise(resolve => {
      const maybePromisedErrors = (this.props.validateSoft as any)(values);
      if (maybePromisedErrors === undefined) {
        resolve({});
      } else if (isPromise(maybePromisedErrors)) {
        (maybePromisedErrors as Promise<any>).then(
          () => {
            resolve({});
          },
          errors => {
            resolve(errors);
          },
        );
      } else {
        resolve(maybePromisedErrors);
      }
    });
  }

  public runValidateHandlerWarning(values: FormikValues): Promise<FormikErrors<Values>> {
    return new Promise(resolve => {
      const maybePromisedErrors = (this.props.validateWarning as any)(values);
      if (maybePromisedErrors === undefined) {
        resolve({});
      } else if (isPromise(maybePromisedErrors)) {
        (maybePromisedErrors as Promise<any>).then(
          () => {
            resolve({});
          },
          errors => {
            resolve(errors);
          },
        );
      } else {
        resolve(maybePromisedErrors);
      }
    });
  }
  /**
   * Run validation against a Yup schema and optionally run a function if successful
   */
  public runValidationSchema = (values: FormikValues) => {
    return new Promise(resolve => {
      const { validationSchema } = this.props;
      const schema = isFunction(validationSchema)
        ? validationSchema()
        : validationSchema;
      validateYupSchema(values, schema).then(
        () => {
          resolve({});
        },
        (err: any) => {
          resolve(yupToFormErrors(err));
        },
      );
    });
  }

  /**
   * Run all validations methods and update state accordingly
   */
  public runValidations = (
    values: FormikValues = this.state.values,
  ): Promise<FormikErrors<Values>> => {
    if (this.validator) {
      this.validator();
    }

    const [promise, cancel] = makeCancelable(
      Promise.all([
        this.runFieldLevelValidations(values),
        this.props.validationSchema ? this.runValidationSchema(values) : {},
        this.props.validate ? this.runValidateHandler(values) : {},
      ]).then(([fieldErrors, schemaErrors, handlerErrors]) => {
        const newFieldErrors = fieldErrors as Partial<FormikErrors<Values>>;
        const newSchemaErrors = schemaErrors as Partial<FormikErrors<Values>>;
        const newHandlerErrors = handlerErrors as Partial<FormikErrors<Values>>;
        return deepmerge.all<FormikErrors<Values>>(
          [newFieldErrors, newSchemaErrors, newHandlerErrors],
          { arrayMerge },
        );
      }),
    );
    this.validator = cancel;
    return promise
      .then((errors: FormikErrors<Values>) => {
        if (this.didMount) {
          this.setState(prevState => {
            if (!isEqual(prevState.errors, errors)) {
              return { errors };
            }
            return null; // abort the update
          });
        }
        return errors;
      })
      .catch(x => x);
  }

  public runValidationsSoft = (
    values: FormikValues = this.state.values,
  ): Promise<FormikErrors<Values>> => {
    if (this.validatorSoft) {
      this.validatorSoft();
    }

    const [promise, cancel] = makeCancelable(
      Promise.all([
        this.runFieldLevelValidationsSoft(values),
        this.props.validateSoft ? this.runValidateHandlerSoft(values) : {},
      ]).then(([fieldErrors, handlerErrors]) => {
        const newFieldErrors = fieldErrors as Partial<FormikErrors<Values>>;
        const newHandlerErrors = handlerErrors as Partial<FormikErrors<Values>>;
        return deepmerge.all<FormikErrors<Values>>(
          [newFieldErrors, newHandlerErrors],
          { arrayMerge },
        );
      }),
    );
    this.validatorSoft = cancel;
    return promise
      .then((errorsSoft: FormikErrors<Values>) => {
        if (this.didMount) {
          this.setState(prevState => {
            if (!isEqual(prevState.errorsSoft, errorsSoft)) {
              return { errorsSoft };
            }
            return null; // abort the update
          });
        }
        return errorsSoft;
      })
      .catch(x => x);
  }

  
  public runValidationsWarning = (
    values: FormikValues = this.state.values,
  ): Promise<FormikErrors<Values>> => {
    if (this.validatorWarning) {
      this.validatorWarning();
    }

    const [promise, cancel] = makeCancelable(
      Promise.all([
        this.runFieldLevelValidationsWarning(values),
        this.props.validateWarning ? this.runValidateHandlerWarning(values) : {},
      ]).then(([fieldErrors, handlerErrors]) => {
        const newFieldErrors = fieldErrors as Partial<FormikErrors<Values>>;
        const newHandlerErrors = handlerErrors as Partial<FormikErrors<Values>>;
        return deepmerge.all<FormikErrors<Values>>(
          [newFieldErrors, newHandlerErrors],
          { arrayMerge },
        );
      }),
    );
    this.validatorWarning = cancel;
    return promise
      .then((warnings: FormikErrors<Values>) => {
        if (this.didMount) {
          this.setState(prevState => {
            if (!isEqual(prevState.warnings, warnings)) {
              return { warnings };
            }
            return null; // abort the update
          });
        }
        return warnings;
      })
      .catch(x => x);
  }

  public handleChange = (
    eventOrPath: string | React.ChangeEvent<any>,
  ): void | ((eventOrValue: unknown | React.ChangeEvent<any>) => void) => {
    // this function actually handles the change
    const executeChange = (
      eventOrValue: unknown | React.ChangeEvent<any>,
      maybePath?: string,
    ) => {
      // To allow using handleChange with React Native (Web) or other UI libraries, we
      // allow for the first argument to be either a value or the standard change event.
      let field = maybePath;
      let value: unknown;
      if (isInputEvent(eventOrValue)) {
        const event = eventOrValue as React.ChangeEvent<any>;
        // If we can, persist the event, https://reactjs.org/docs/events.html#event-pooling
        if (event.persist) {
          event.persist();
        }
        const { type, name, id, checked, outerHTML } = event.target;
        field = maybePath ? maybePath : name ? name : id;
        if (!field && process.env.NODE_ENV !== 'production') {
          warnAboutMissingIdentifier({
            htmlContent: outerHTML,
            documentationAnchorLink: 'handlechange-e-reactchangeeventany--void',
            handlerName: 'handleChange',
          });
        }
        value = event.target.value;
        if (/number|range/.test(type)) {
          const parsed = parseFloat(event.target.value);
          value = isNaN(parsed) ? '' : parsed;
        }
        if (/checkbox/.test(type)) {
          value = checked;
        }
      } else {
        value = eventOrValue;
      }

      if (field) {
        // Set form fields by name
        this.setState(
          prevState => ({
            ...prevState,
            isLastSubmitInvalid: false,
            values: setIn(prevState.values, field!, value),
          }),
          () => {
            if (this.props.validateOnChange) {
              this.runValidations(setIn(this.state.values, field!, value));
              this.runValidationsSoft(setIn(this.state.values, field!, value));
              this.runValidationsWarning(setIn(this.state.values, field!, value));
            }
          },
        );
      }
    };

    // Actually execute logic above....
    if (isString(eventOrPath)) {
      const path = eventOrPath;
      // cache these handlers by key like Preact's linkState does for perf boost
      if (!isFunction(this.hcCache[path])) {
        // set a new handle function in cache
        this.hcCache[path] = (eventOrValue: unknown | React.ChangeEvent<any>) =>
          executeChange(eventOrValue, path);
      }
      return this.hcCache[path]; // return the cached function
    } {
      const event = eventOrPath;
      executeChange(event);
    }
  }

  public setFieldValue = (
    field: string,
    value: any,
    shouldValidate: boolean = true,
  ) => {
    if (this.didMount) {
      // Set form field by name
      this.setState(
        prevState => ({
          ...prevState,
          values: setIn(prevState.values, field, value),
        }),
        () => {
          if (this.props.validateOnChange && shouldValidate) {
            this.runValidations(this.state.values);
            this.runValidationsSoft(this.state.values);
            this.runValidationsWarning(this.state.values);
          }
        },
      );
    }
  }

  public handleSubmit = (e: React.FormEvent<HTMLFormElement> | undefined) => {
    if (e && e.preventDefault) {
      e.preventDefault();
    }

    // Warn if form submission is triggered by a <button> without a
    // specified `type` attribute during development. This mitigates
    // a common gotcha in forms with both reset and submit buttons,
    // where the dev forgets to add type="button" to the reset button.
    if (
      process.env.NODE_ENV !== 'production' &&
      typeof document !== 'undefined'
    ) {
      // Safely get the active element (works with IE)
      const activeElement = getActiveElement();
      if (
        activeElement !== null &&
        activeElement instanceof HTMLButtonElement
      ) {
        warning(
          !!(
            activeElement.attributes &&
            activeElement.attributes.getNamedItem('type')
          ),
          'You submitted a Formik form using a button with an unspecified `type` attribute.  Most browsers default button elements to `type="submit"`. If this is not a submit button, please add `type="button"`.',
        );
      }
    }

    this.submitForm();
  }

  public submitForm = () => {
    // Recursively set all values to `true`.
    this.setState(prevState => ({
      touched: setNestedObjectValues<FormikTouched<Values>>(
        prevState.values,
        true,
      ),
      isSubmitting: true,
      isValidating: true,
      isLastSubmitInvalid: false,
      submitCount: prevState.submitCount + 1,
    }));

    this.runValidationsSoft(this.state.values);
    this.runValidationsWarning(this.state.values);
    return this.runValidations(this.state.values).then(combinedErrors => {
      if (this.didMount) {
        this.setState({ isValidating: false });
      }
      const isValid = Object.keys(combinedErrors).length === 0;
      if (isValid) {
        this.executeSubmit();
      } else if (this.didMount) {
        // ^^^ Make sure Formik is still mounted before calling setState
        this.setState({ isSubmitting: false, isLastSubmitInvalid: true });
      }
    });
  }

  public executeSubmit = () => {
    this.props.onSubmit(this.state.values, this.getFormikActions());
  }

  public handleBlur = (
    eventOrPath: string | React.FocusEvent<any>,
  ): void | ((e?: React.FocusEvent<any>) => void) => {
    const executeBlur = (
      maybeEvent?: React.FocusEvent<any>,
      maybePath?: string,
    ) => {
      let field = maybePath;
      if (isInputEvent(maybeEvent)) {
        const event = maybeEvent as React.FocusEvent<any>;
        // If we can, persist the event, https://reactjs.org/docs/events.html#event-pooling
        if (event.persist) {
          event.persist();
        }
        const { name, id, outerHTML } = event.target;
        field = name ? name : id;
        if (!field && process.env.NODE_ENV !== 'production') {
          warnAboutMissingIdentifier({
            htmlContent: outerHTML,
            documentationAnchorLink: 'handleblur-e-reactfocuseventany--void',
            handlerName: 'handleBlur',
          });
        }
      }

      this.setState(prevState => ({
        touched: setIn(prevState.touched, field!, true),
      }));

      if (this.props.validateOnBlur) {
        this.runValidations(this.state.values);
        this.runValidationsSoft(this.state.values);
        this.runValidationsWarning(this.state.values);
      }
    };
    if (isString(eventOrPath)) {
      const path = eventOrPath;
      // cache these handlers by key like Preact's linkState does for perf boost
      if (!isFunction(this.hbCache[path])) {
        // set a new handle function in cache
        this.hbCache[path] = (event?: React.FocusEvent<any>) =>
          executeBlur(event, path);
      }
      return this.hbCache[path]; // return the cached function
    } {
      const event = eventOrPath;
      executeBlur(event);
    }
  }

  public setFieldTouched = (
    field: string,
    touched: boolean = true,
    shouldValidate: boolean = true,
  ) => {
    // Set touched field by name
    this.setState(
      prevState => ({
        ...prevState,
        touched: setIn(prevState.touched, field, touched),
      }),
      () => {
        if (this.props.validateOnBlur && shouldValidate) {
          this.runValidations(this.state.values);
          this.runValidationsSoft(this.state.values);
          this.runValidationsWarning(this.state.values);
        }
      },
    );
  }

  public setFieldError = (field: string, message: string | undefined) => {
    // Set form field by name
    this.setState(prevState => ({
      ...prevState,
      errors: setIn(prevState.errors, field, message),
    }));
  }

  public resetForm = (nextValues?: Values) => {
    const values = nextValues ? nextValues : this.props.initialValues;

    this.initialValues = values;

    this.setState({
      isSubmitting: false,
      isLastSubmitInvalid: false,
      isValidating: false,
      errors: {},
      errorsSoft: {},
      warnings: {},
      touched: {},
      error: undefined,
      status: this.props.initialStatus,
      values,
      submitCount: 0,
    });
  }

  public handleReset = () => {
    if (this.props.onReset) {
      const maybePromisedOnReset = (this.props.onReset as any)(
        this.state.values,
        this.getFormikActions(),
      );

      if (isPromise(maybePromisedOnReset)) {
        (maybePromisedOnReset as Promise<any>).then(this.resetForm);
      } else {
        this.resetForm();
      }
    } else {
      this.resetForm();
    }
  }

  public setFormikState = (s: any, callback?: (() => void)) =>
    this.setState(s, callback)

  public validateForm = (values: Values) => {
    this.setState({ isValidating: true });
    this.runValidationsSoft(this.state.values);
    this.runValidationsWarning(this.state.values);
    return this.runValidations(values).then(errors => {
      if (this.didMount) {
        this.setState({ isValidating: false });
      }
      return errors;
    });
  }

  public getFormikActions = (): FormikActions<Values> => {
    return {
      resetForm: this.resetForm,
      submitForm: this.submitForm,
      validateForm: this.validateForm,
      validateField: this.validateField,
      setError: this.setError,
      setErrors: this.setErrors,
      setFieldError: this.setFieldError,
      setFieldTouched: this.setFieldTouched,
      setFieldValue: this.setFieldValue,
      setStatus: this.setStatus,
      setSubmitting: this.setSubmitting,
      setTouched: this.setTouched,
      setValues: this.setValues,
      setFormikState: this.setFormikState,
    };
  }

  public getFormikComputedProps = () => {
    const { isInitialValid } = this.props;
    const dirty = !isEqual(this.initialValues, this.state.values);
    return {
      dirty,
      isValid: dirty
        ? this.state.errors && Object.keys(this.state.errors).length === 0
        : isInitialValid !== false && isFunction(isInitialValid)
          ? (isInitialValid as (props: this['props']) => boolean)(this.props)
          : (isInitialValid as boolean),
      initialValues: this.initialValues,
    };
  }

  public getFormikBag = () => {
    return {
      ...this.state,
      ...this.getFormikActions(),
      ...this.getFormikComputedProps(),
      // Field needs to communicate with Formik during resets
      registerField: this.registerField,
      unregisterField: this.unregisterField,
      handleBlur: this.handleBlur,
      handleChange: this.handleChange,
      handleReset: this.handleReset,
      handleSubmit: this.handleSubmit,
      validateOnChange: this.props.validateOnChange,
      validateOnBlur: this.props.validateOnBlur,
    };
  }

  public getFormikContext = () => {
    return {
      ...this.getFormikBag(),
      validationSchema: this.props.validationSchema,
      validate: this.props.validate,
      initialValues: this.initialValues,
    };
  }

  public render() {
    const { component, render, children } = this.props;
    const props = this.getFormikBag();
    const ctx = this.getFormikContext();
    return (
      <FormikProvider value={ctx}>
        {component
          ? React.createElement(component as any, props)
          : render
            ? render(props)
            : children // children come last, always called
              ? isFunction(children)
                ? (children as ((
                  props: FormikProps<Values>,
                ) => React.ReactNode))(props as FormikProps<Values>)
                : !isEmptyChildren(children)
                  ? React.Children.only(children)
                  : null
              : null}
      </FormikProvider>
    );
  }
}

function warnAboutMissingIdentifier({
  htmlContent,
  documentationAnchorLink,
  handlerName,
}: {
  htmlContent: string;
  documentationAnchorLink: string;
  handlerName: string;
}) {
  console.warn(
    `Warning: Formik called \`${handlerName}\`, but you forgot to pass an \`id\` or \`name\` attribute to your input:

    ${htmlContent}

    Formik cannot determine which value to update. For more info see https://github.com/jaredpalmer/formik#${documentationAnchorLink}
  `,
  );
}

/**
 * Transform Yup ValidationError to a more usable object
 */
export function yupToFormErrors<Values>(yupError: any): FormikErrors<Values> {
  let errors: any = {} as FormikErrors<Values>;
  if (yupError.inner.length === 0) {
    return setIn(errors, yupError.path, yupError.message);
  }
  for (const err of yupError.inner) {
    if (!errors[err.path]) {
      errors = setIn(errors, err.path, err.message);
    }
  }
  return errors;
}

/**
 * Validate a yup schema.
 */
export function validateYupSchema<T extends FormikValues>(
  values: T,
  schema: any,
  sync: boolean = false,
  context: any = {},
): Promise<Partial<T>> {
  const validateData: Partial<T> = {};
  for (const k in values) {
    //if (values.hasOwnProperty(k)) {
    if (has(values, k)) {
      const key = String(k) as keyof T;
      validateData[key] = values[key] !== '' ? values[key] : undefined;
    }
  }
  return schema[sync ? 'validateSync' : 'validate'](validateData, {
    abortEarly: false,
    context,
  });
}

/**
 * deepmerge array merging algorithm
 * https://github.com/KyleAMathews/deepmerge#combine-array
 */
function arrayMerge(target: any[], source: any[], options: any): any[] {
  const destination = target.slice();

  source.forEach(function (e: any, i: number) {
    if (typeof destination[i] === 'undefined') {
      const cloneRequested = options.clone !== false;
      const shouldClone = cloneRequested && options.isMergeableObject(e);
      destination[i] = shouldClone
        ? deepmerge(Array.isArray(e) ? [] : {}, e, options)
        : e;
    } else if (options.isMergeableObject(e)) {
      destination[i] = deepmerge(target[i], e, options);
    } else if (target.indexOf(e) === -1) {
      destination.push(e);
    }
  });
  return destination;
}
